import React, { useMemo } from 'react';
import { Table, Tag, Typography, Space, Tooltip } from 'antd';
import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import RouteInstanceBracket from './RouteInstanceBracket';

const { Text } = Typography;

const BRACKET_COLORS = {
    complete: '#1890ff',
    incomplete: '#fa8c16',
    pendingNextBatch: '#bfbfbf',
    unmatched: '#ff4d4f',
};

/**
 * 把某个时间字段解析成可比较的数值；无值 / 非法值统一排到最后。
 */
const timeValue = (t) => {
    if (!t) return Number.POSITIVE_INFINITY;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
};

/**
 * 把 route_instances + anomalies 打平成一维行数组
 * 每行对应一个 leg，或一个缺失航段占位行，或一个未匹配航班行
 *
 * 排序策略：把「匹配航线实例」和「每个未匹配航班」都当作一个 group，各自取一个
 * 时间键（实例取其最早航段的 ATD，未匹配取该航班 ATD），按时间统一升序排。
 * 这样未匹配航班会按时间插到它该在的位置，而不是统一堆在最下面；同时实例内部的
 * 航段仍然连续、括号编号（_groupIdx）跟着最终可视顺序走。
 */
const buildFlatRows = (instances, anomalies) => {
    const anomalyByInstance = {};
    const unmatchedAnomalies = [];
    (anomalies || []).forEach(a => {
        if (a.anomaly_type === 'UNMATCHED_FLIGHT') {
            unmatchedAnomalies.push(a);
        } else if (a.route_instance_id) {
            if (!anomalyByInstance[a.route_instance_id]) anomalyByInstance[a.route_instance_id] = [];
            anomalyByInstance[a.route_instance_id].push(a);
        }
    });

    // 先把匹配实例与未匹配航班都收敛成带时间键的 group
    const groups = [];

    (instances || []).forEach(inst => {
        const flights = inst.flights || [];
        const instAnomalies = anomalyByInstance[inst.instance_id] || [];
        const isPending = instAnomalies.some(a => a.status === 'PENDING_NEXT_BATCH');
        const bracketColor = isPending
            ? BRACKET_COLORS.pendingNextBatch
            : inst.is_complete
                ? BRACKET_COLORS.complete
                : BRACKET_COLORS.incomplete;
        const missingLegs = instAnomalies.flatMap(a => a.missing_legs || []);
        const sortKey = flights.reduce(
            (min, leg) => Math.min(min, timeValue(leg.atd)),
            Number.POSITIVE_INFINITY,
        );
        groups.push({ _kind: 'instance', _sort: sortKey, inst, flights, missingLegs, isPending, bracketColor });
    });

    unmatchedAnomalies.forEach(a => {
        (a.unmatched_flights || []).forEach((uf, idx) => {
            groups.push({ _kind: 'unmatched', _sort: timeValue(uf.atd), anomaly: a, uf, ufIdx: idx });
        });
    });

    // 用大小比较而非相减，避免两个都无时间(Infinity)时相减得到 NaN 导致排序不稳定
    groups.sort((g1, g2) => (g1._sort === g2._sort ? 0 : g1._sort < g2._sort ? -1 : 1));

    const rows = [];
    let groupIdx = 0;

    groups.forEach(group => {
        groupIdx++;

        if (group._kind === 'instance') {
            const { inst, flights, missingLegs, isPending, bracketColor } = group;
            const totalRows = flights.length + missingLegs.length;

            flights.forEach((leg, legIdx) => {
                const isFirst = legIdx === 0;
                const isLast = legIdx === flights.length - 1 && missingLegs.length === 0;
                let position = 'middle';
                if (totalRows === 1) position = 'single';
                else if (isFirst) position = 'start';
                else if (isLast) position = 'end';

                rows.push({
                    _key: `leg-${inst.instance_id}-${legIdx}`,
                    _type: 'leg',
                    _groupIdx: groupIdx,
                    _bracketPosition: position,
                    _bracketColor: bracketColor,
                    _routeCode: inst.route_code,
                    _isComplete: inst.is_complete,
                    _isPending: isPending,
                    _totalCost: isFirst ? inst.total_cost : null,
                    _instanceId: inst.instance_id,
                    _legCount: flights.length,
                    flight_number: leg.flight_number,
                    departure_airport: leg.departure_airport,
                    arrival_airport: leg.arrival_airport,
                    atd: leg.atd,
                    ata: leg.ata,
                    total_bh: leg.total_bh,
                    total_cost: leg.total_cost,
                    leg_sequence: leg.leg_sequence,
                });
            });

            missingLegs.forEach((ml, mlIdx) => {
                const isLast = mlIdx === missingLegs.length - 1;
                let position = 'middle';
                if (flights.length === 0 && missingLegs.length === 1) position = 'single';
                else if (flights.length === 0 && mlIdx === 0) position = 'start';
                if (isLast) position = totalRows === 1 ? 'single' : 'end';

                rows.push({
                    _key: `missing-${inst.instance_id}-${mlIdx}`,
                    _type: 'missing',
                    _groupIdx: groupIdx,
                    _bracketPosition: position,
                    _bracketColor: bracketColor,
                    _routeCode: inst.route_code,
                    _isComplete: false,
                    _isPending: isPending,
                    _instanceId: inst.instance_id,
                    departure_airport: ml.dep,
                    arrival_airport: ml.arr,
                });
            });
        } else {
            const { anomaly: a, uf, ufIdx } = group;
            rows.push({
                _key: `unmatched-${a.id}-${ufIdx}`,
                _type: 'unmatched',
                _groupIdx: groupIdx,
                _bracketPosition: 'none',
                _bracketColor: BRACKET_COLORS.unmatched,
                _routeCode: null,
                _isComplete: false,
                _isPending: a.status === 'PENDING_NEXT_BATCH',
                _anomalyStatus: a.status,
                _severity: a.severity,
                flight_number: uf.flight_number,
                departure_airport: uf.leg?.split('-')[0] || '',
                arrival_airport: uf.leg?.split('-')[1] || '',
                atd: uf.atd,
                aircraft_reg: uf.aircraft_reg,
            });
        }
    });

    return rows;
};

const formatTime = (time) => {
    if (!time) return '-';
    const d = new Date(time);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');
    return `${mm}-${dd} ${hh}:${min}`;
};

const RouteInstanceTable = ({ instances, anomalies }) => {
    const flatRows = useMemo(() => buildFlatRows(instances, anomalies), [instances, anomalies]);

    const columns = [
        {
            title: '',
            key: 'bracket',
            width: 32,
            align: 'center',
            className: 'bracket-col',
            render: (_, row) => (
                <RouteInstanceBracket
                    position={row._bracketPosition}
                    color={row._bracketColor}
                    label={row._bracketPosition === 'start' || row._bracketPosition === 'single' ? row._groupIdx : null}
                    tooltip={row._routeCode || undefined}
                />
            ),
        },
        {
            title: '航线',
            key: 'route_code',
            width: 120,
            render: (_, row) => {
                if (row._type === 'missing') {
                    return row._routeCode ? <Text type="secondary">{row._routeCode}</Text> : null;
                }
                if (row._type === 'unmatched') return <Tag color="red">未匹配</Tag>;
                if (!row._routeCode) return null;
                return (
                    <Space size={4}>
                        <Text strong>{row._routeCode}</Text>
                        {!row._isComplete && !row._isPending && <WarningOutlined style={{ color: '#fa8c16', fontSize: 12 }} />}
                        {row._isPending && <ClockCircleOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />}
                    </Space>
                );
            },
        },
        {
            title: '航班号',
            dataIndex: 'flight_number',
            key: 'flight_number',
            width: 90,
            render: (v, row) => {
                if (row._type === 'missing') return <Text type="secondary" italic>缺失航段</Text>;
                return v || '-';
            },
        },
        {
            title: '航段',
            key: 'segment',
            width: 100,
            render: (_, row) => {
                if (row._type === 'missing') {
                    return (
                        <Text type="secondary" italic>
                            {row.departure_airport} → {row.arrival_airport}
                        </Text>
                    );
                }
                return (
                    <span>
                        <Text strong>{row.departure_airport}</Text>
                        <span style={{ color: '#bfbfbf', margin: '0 2px' }}>-</span>
                        <Text strong>{row.arrival_airport}</Text>
                    </span>
                );
            },
        },
        {
            title: 'ATD',
            dataIndex: 'atd',
            key: 'atd',
            width: 110,
            render: (v, row) => row._type === 'missing' ? null : <Text className="monospaced-font">{formatTime(v)}</Text>,
        },
        {
            title: 'ATA',
            dataIndex: 'ata',
            key: 'ata',
            width: 110,
            render: (v, row) => row._type === 'missing' ? null : <Text className="monospaced-font">{formatTime(v)}</Text>,
        },
        {
            title: '成本',
            key: 'cost',
            width: 100,
            align: 'right',
            render: (_, row) => {
                if (row._type === 'missing' || row._type === 'unmatched') return null;
                const cost = Number(row.total_cost || 0);
                return <Text>${cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>;
            },
        },
        {
            title: '实例合计',
            key: 'instance_total',
            width: 110,
            align: 'right',
            render: (_, row) => {
                if (row._totalCost == null) return null;
                const total = Number(row._totalCost);
                return (
                    <Text strong style={{ color: '#52c41a' }}>
                        ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                );
            },
        },
    ];

    return (
        <>
            <style>{`
                .route-instance-table .missing-leg-row td {
                    background: #fafafa !important;
                    border-bottom: 1px dashed #d9d9d9 !important;
                }
                .route-instance-table .unmatched-flight-row td {
                    background: #fff2f0 !important;
                }
                .route-instance-table .bracket-col {
                    padding: 0 !important;
                }
                .route-instance-table .ant-table-cell {
                    padding: 4px 8px !important;
                }
            `}</style>
            <Table
                className="route-instance-table"
                size="small"
                columns={columns}
                dataSource={flatRows}
                rowKey="_key"
                pagination={false}
                rowClassName={(row) => {
                    if (row._type === 'missing') return 'missing-leg-row';
                    if (row._type === 'unmatched') return 'unmatched-flight-row';
                    return '';
                }}
                style={{ marginTop: 8 }}
            />
        </>
    );
};

export default RouteInstanceTable;
